import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CaretDown, CaretRight, Lightbulb, NotePencil, Paperclip, Sparkle, SquaresFour, Wrench, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { inputStyle, textareaClass } from '@/lib/styleKit'
import { cn } from '@/lib/utils'
import { adminMetaApi, adminSuggestionsApi } from '../adminApi'
import {
  DEFAULT_SUGGESTION_FILTERS,
  type SuggestionFilters,
  groupByArea,
  hasActiveSuggestionFilters,
  suggestionFiltersFromSearch,
  searchFromSuggestionFilters,
  toSuggestionListParams,
} from '../lib/suggestionFilters'
import { formatDate, suggestionStatusLabel } from '../lib/labels'
import {
  ActivityLog,
  Badge,
  EmptyState,
  ErrorBanner,
  Eyebrow,
  FilterPill,
  IMPORTANCE_TONE,
  PageHeader,
  Panel,
  bodyStyle,
  metaStyle,
} from '../components/ui'
import type { SuggestionItem, SuggestionPatch, SuggestionStatus } from '../types'

const ALL_STATUSES: SuggestionStatus[] = ['new', 'under_review', 'planned', 'done', 'declined']

const KIND: Record<string, { label: string; icon: typeof Wrench; tone: 'info' | 'violet' }> = {
  improve_existing: { label: 'Improve', icon: Wrench, tone: 'info' },
  new_feature: { label: 'New feature', icon: Sparkle, tone: 'violet' },
}

// ----------------------------------------------------------------- note

function InternalNote({ value, busy, onSave }: { value: string | null; busy: boolean; onSave: (next: string | null) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')

  const start = () => {
    setDraft(value ?? '')
    setEditing(true)
  }

  if (!editing) {
    return (
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Eyebrow>Internal note</Eyebrow>
          {value ? (
            <p className="mt-1 whitespace-pre-wrap text-foreground dark:text-[#f7f8f8]" style={bodyStyle}>
              {value}
            </p>
          ) : (
            <p className="mt-1 text-muted-foreground dark:text-[#8a8f98]" style={bodyStyle}>
              No note yet.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={start}
          className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline shrink-0"
          style={metaStyle}
        >
          <NotePencil size={13} />
          {value ? 'Edit' : 'Add note'}
        </button>
      </div>
    )
  }

  return (
    <div>
      <Eyebrow>Internal note</Eyebrow>
      <textarea
        autoFocus
        className={cn(textareaClass, 'mt-1 min-h-[88px]')}
        style={inputStyle}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Team only. Never shown to the reporter."
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={busy}
          disabled={(draft.trim() || '') === (value ?? '')}
          onClick={() => {
            onSave(draft.trim() || null)
            setEditing(false)
          }}
        >
          Save note
        </Button>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------- row

function SuggestionRow({
  item,
  showArea,
  onPatch,
  busy,
}: {
  item: SuggestionItem
  showArea: boolean
  onPatch: (patch: SuggestionPatch) => void
  busy: boolean
}) {
  const [open, setOpen] = useState(false)
  const kind = KIND[item.subtype ?? ''] ?? { label: 'Suggestion', icon: Lightbulb, tone: 'info' as const }
  const KindIcon = kind.icon
  const statusOptions = ALL_STATUSES.map((s) => ({ value: s, label: suggestionStatusLabel(s) }))
  const toggle = () => setOpen((v) => !v)

  return (
    <li className={cn('group', open && 'bg-muted/30 dark:bg-white/[0.02]')}>
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
        onClick={toggle}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <span className="mt-1 text-muted-foreground/70 group-hover:text-foreground">
          {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
        </span>

        <div className="min-w-0 flex-1">
          <p className={cn('text-foreground dark:text-[#f7f8f8]', open ? 'whitespace-pre-wrap' : 'line-clamp-2')} style={bodyStyle}>
            {item.text}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={kind.tone}>
              <KindIcon size={11} weight="bold" />
              {kind.label}
            </Badge>
            {showArea && <Badge>{item.module ?? 'No area'}</Badge>}
            {item.importance && <Badge tone={IMPORTANCE_TONE[item.importance] ?? 'neutral'}>{item.importance}</Badge>}
            {item.attachment_url && (
              <Badge>
                <Paperclip size={11} /> Attachment
              </Badge>
            )}
            {item.internal_notes && !open && (
              <Badge>
                <NotePencil size={11} /> Note
              </Badge>
            )}
            <span className="ml-1 text-muted-foreground dark:text-[#8a8f98] truncate" style={metaStyle}>
              {item.reporter.email ?? `user ${item.reporter.user_id}`}
              {item.reporter.account_name && <> · {item.reporter.account_name}</>}
              {' · '}
              {formatDate(item.created_at)}
            </span>
          </div>
        </div>

        <div className="w-36 shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            ariaLabel="Status"
            value={item.admin_status}
            onChange={(v) => onPatch({ admin_status: v as SuggestionStatus })}
            options={statusOptions}
            buttonClassName="h-8"
          />
        </div>
      </div>

      {open && (
        <div className="ml-11 mr-4 mb-4 rounded-md border border-border/50 dark:border-white/[0.06] bg-background dark:bg-white/[0.02]">
          {(item.problem_solved || item.attachment_url) && (
            <div className="px-4 py-3 space-y-3 border-b border-border/50 dark:border-white/[0.06]">
              {item.problem_solved && (
                <div>
                  <Eyebrow>Problem it solves</Eyebrow>
                  <p className="mt-1 whitespace-pre-wrap text-foreground dark:text-[#f7f8f8]" style={bodyStyle}>
                    {item.problem_solved}
                  </p>
                </div>
              )}
              {item.attachment_url && (
                <a
                  href={item.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline"
                  style={bodyStyle}
                >
                  <Paperclip size={13} /> Open attachment
                </a>
              )}
            </div>
          )}
          <div className="px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
            <InternalNote value={item.internal_notes} busy={busy} onSave={(next) => onPatch({ internal_notes: next })} />
          </div>
          <div className="px-4 py-3">
            <Eyebrow className="mb-2">Activity</Eyebrow>
            <ActivityLog entries={item.activity} statusLabel={suggestionStatusLabel} />
          </div>
        </div>
      )}
    </li>
  )
}

// ----------------------------------------------------------------- page

export default function SuggestionsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => suggestionFiltersFromSearch(searchParams), [searchParams])
  const apply = (next: SuggestionFilters) => setSearchParams(searchFromSuggestionFilters(next), { replace: true })
  const set = (patch: Partial<SuggestionFilters>) => apply({ ...filters, ...patch, page: 1 })
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const meta = useQuery({ queryKey: ['admin', 'meta'], queryFn: () => adminMetaApi.get().then((r) => r.data) })
  const list = useQuery({
    queryKey: ['admin', 'suggestions', toSuggestionListParams(filters)],
    queryFn: () => adminSuggestionsApi.list(toSuggestionListParams(filters)).then((r) => r.data),
    keepPreviousData: true,
  })

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: SuggestionPatch }) =>
      adminSuggestionsApi.patch(id, patch).then((r) => r.data),
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['admin', 'suggestions'] })
    },
    onError: () => setError('Could not save the change.'),
  })

  const counts = list.data?.counts ?? {}
  const allCount = ALL_STATUSES.reduce((n, s) => n + (counts[s] ?? 0), 0)
  const moduleOptions = [{ value: '', label: 'All areas' }, ...(meta.data?.modules ?? []).map((m) => ({ value: m, label: m }))]
  const subtypeOptions = [
    { value: '', label: 'Both kinds' },
    { value: 'improve_existing', label: 'Improve existing' },
    { value: 'new_feature', label: 'New feature' },
  ]
  const items = list.data?.items ?? []
  const groups = filters.grouped ? groupByArea(items) : null

  const renderRows = (rows: SuggestionItem[], showArea: boolean) => (
    <ul className="divide-y divide-border/50 dark:divide-white/[0.06]">
      {rows.map((item) => (
        <SuggestionRow
          key={item.id}
          item={item}
          showArea={showArea}
          busy={save.isLoading && save.variables?.id === item.id}
          onPatch={(patch) => save.mutate({ id: item.id, patch })}
        />
      ))}
    </ul>
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title="Suggestions"
        description="What users want changed or added, newest first. Group by area to see where change is wanted most."
        actions={
          <Button
            type="button"
            variant={filters.grouped ? 'default' : 'outline'}
            size="sm"
            icon={<SquaresFour size={14} />}
            onClick={() => set({ grouped: !filters.grouped })}
            aria-pressed={filters.grouped}
          >
            Group by area
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
          <FilterPill active={filters.status === ''} onClick={() => set({ status: '' })} count={allCount}>
            All
          </FilterPill>
          {ALL_STATUSES.map((s) => (
            <FilterPill key={s} active={filters.status === s} onClick={() => set({ status: s })} count={counts[s] ?? 0}>
              {suggestionStatusLabel(s)}
            </FilterPill>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select ariaLabel="Area" value={filters.module} onChange={(v) => set({ module: v })} options={moduleOptions} className="w-40" />
          <Select ariaLabel="Kind" value={filters.subtype} onChange={(v) => set({ subtype: v })} options={subtypeOptions} className="w-40" />
          {hasActiveSuggestionFilters(filters) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => apply({ ...DEFAULT_SUGGESTION_FILTERS, grouped: filters.grouped })}
              icon={<X size={14} />}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Panel className="overflow-hidden">
        {list.isLoading && (
          <div className="py-14 flex justify-center">
            <DotLoader />
          </div>
        )}
        {list.isError && (
          <EmptyState>
            Could not load suggestions.{' '}
            <button type="button" className="underline" onClick={() => list.refetch()}>
              Retry
            </button>
          </EmptyState>
        )}
        {list.data && items.length === 0 && (
          <EmptyState icon={<Lightbulb size={26} />}>
            {hasActiveSuggestionFilters(filters) ? 'No suggestions match these filters.' : 'No suggestions yet.'}
          </EmptyState>
        )}
        {list.data && items.length > 0 && !groups && renderRows(items, true)}
        {list.data && items.length > 0 && groups && (
          <div className="divide-y divide-border/50 dark:divide-white/[0.06]">
            {groups.map((g) => (
              <section key={g.area}>
                <header className="flex items-center justify-between bg-muted/50 dark:bg-white/[0.03] px-4 h-8">
                  <Eyebrow className="text-foreground/80 dark:text-[#f7f8f8]/80">{g.area}</Eyebrow>
                  <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums" style={metaStyle}>
                    {g.items.length}
                  </span>
                </header>
                {renderRows(g.items, false)}
              </section>
            ))}
          </div>
        )}
      </Panel>
    </div>
  )
}
